import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	type DismissCallback,
	useContextScope,
	useMessagePlacementHooks,
} from '@atlassian/post-office-context';
import { MaybeSuspenseBoundary } from '@post-office/placement-common/utils/message-renderer';
import { useInitialData } from '@post-office/placement-contexts/hooks';
import { usePlacementApi } from '@post-office/use-placement-api';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { EXPERIMENTAL_MESSAGES } from './experimental-messages-list';
import { scopes } from './scopes';
import { SuppressIfModalIsPresent } from './suppress-if-modal-is-present';
import { useEpMigrationTriggers } from './use-ep-migration-triggers';
import { MessageRenderer } from '../__generated__/client/render';
import { type ScreenSpaceFlagsMessageState } from '../api/types';
import type { Placement as ScreenSpaceFlagsPlacement } from '../contracts/Placement';
import { usePostOfficeSearchParams } from './use-post-office-search-params';

export const ScreenSpaceFlagsPlacementNewDismissals = ({
	initialData,
	preloadedInitialData,
	setPreloadedInitialData,
	jiraFlagsService,
	reportExpectedError,
	reportUnexpectedError,
}: ScreenSpaceFlagsPlacement & {
	initialData: Promise<Response | undefined> | undefined;
}) => {
	const isEPExternalMessageSSFEnabled = FeatureGates.checkGate(
		'post-office_enable_ep_external_message_web',
	);

	const isEPExternalMessageSSREnabled = FeatureGates.checkGate(
		'post-office_enabled_ep_external_message_ssr',
	);

	const [localMessageState, setLocalMessageState] = useState<
		ScreenSpaceFlagsMessageState[] | undefined
	>(() => {
		if (preloadedInitialData) {
			return preloadedInitialData?.messages?.length > 0
				? preloadedInitialData?.messages
				: undefined;
		}

		return undefined;
	});

	const [localExternalMessageState, setLocalExternalMessageState] = useState<
		ScreenSpaceFlagsMessageState[] | undefined
	>(() => {
		// Initialize from preloadedInitialData only if both feature gates are enabled
		if (
			isEPExternalMessageSSREnabled &&
			isEPExternalMessageSSFEnabled &&
			preloadedInitialData?.externalMessages
		) {
			return preloadedInitialData.externalMessages.length > 0
				? preloadedInitialData.externalMessages
				: undefined;
		}
		return undefined;
	});

	// Dismissals are stored locally to keep the local state relatively immutable,
	// except unless explicitly refreshed from the server.
	const [dismissedMessageInstanceIds, setDismissedMessageInstanceIds] = useState<string[]>([]);

	// Filter the messages at runtime to ensure they are always accurate and
	// so we don't need to manage the dismissed messages in the state.
	const { filteredMessages, filteredExternalMessages } = useMemo(() => {
		return {
			filteredMessages: localMessageState?.filter(
				(msg) => !dismissedMessageInstanceIds.includes(msg.messageInstanceId),
			),
			filteredExternalMessages: localExternalMessageState?.filter(
				(msg) => !dismissedMessageInstanceIds.includes(msg.messageInstanceId),
			),
		};
	}, [localMessageState, localExternalMessageState, dismissedMessageInstanceIds]);

	useInitialData(initialData);

	const { onMessageDismiss, onMessageClicked, onMessageSnooze } = useMessagePlacementHooks();

	const handleDismiss = useCallback<DismissCallback>(
		({ messageInstanceId }: { messageInstanceId: string }) => {
			if (messageInstanceId) {
				// dismiss the current message instance
				setDismissedMessageInstanceIds((current) => [...current, messageInstanceId]);
			} else {
				// eslint-disable-next-line no-console
				console.error(
					`A valid messageInstanceId is required to dismiss a message: ${messageInstanceId}`,
				);
			}
		},
		[],
	);

	const onDismissed = useCallback(
		(messageInstanceId: string) => {
			handleDismiss({ messageInstanceId });
		},
		[handleDismiss],
	);

	useEffect(() => onMessageDismiss(handleDismiss), [onMessageDismiss]);
	useEffect(() => onMessageClicked(handleDismiss), [onMessageClicked]);
	useEffect(() => onMessageSnooze(handleDismiss), [onMessageSnooze]);

	useEffect(() => {
		// update the preloaded initial data with the accurate filtered messages
		if (typeof setPreloadedInitialData === 'function') {
			if (isEPExternalMessageSSREnabled && isEPExternalMessageSSFEnabled) {
				setPreloadedInitialData({
					messages: filteredMessages ?? [],
					externalMessages: filteredExternalMessages ?? [],
				});
			} else {
				setPreloadedInitialData({
					messages: filteredMessages ?? [],
				});
			}
		}
	}, [
		filteredMessages,
		filteredExternalMessages,
		setPreloadedInitialData,
		isEPExternalMessageSSREnabled,
		isEPExternalMessageSSFEnabled,
	]);

	const activeScopes = useContextScope({
		scopes,
		treatIntersectionAsSame: FeatureGates.checkGate('post-office-ssf-treat-intersection-as-same'),
	});

	const searchParams = usePostOfficeSearchParams();
	const messagesState = usePlacementApi<{
		messages: ScreenSpaceFlagsMessageState[];
		externalMessages?: ScreenSpaceFlagsMessageState[];
	}>('/update-messages', {
		queryParams: {
			...searchParams,
			updateKey: activeScopes.updateKey ?? '',
		},
		requestOptions: { method: 'POST', body: JSON.stringify(activeScopes) },
		throttleTime: 100,
		autoRefetch: {
			isEnabled: FeatureGates.checkGate('post-office-ssf-enable-continuous-auto-refetch')
				? !!activeScopes.updateKey && !filteredMessages?.length
				: !!activeScopes.updateKey && typeof filteredMessages === 'undefined',
			updateKey: activeScopes.updateKey ?? '',
		},
	});

	useEffect(() => {
		if (messagesState?.data?.messages?.length) {
			const messages = messagesState.data.messages;
			let selectedMessage: ScreenSpaceFlagsMessageState | undefined;

			// New experiments in the screen-space-flags placement must be added to the EXPERIMENTAL_MESSAGES list
			for (const message of messages) {
				const experimentalMessage = EXPERIMENTAL_MESSAGES.find(
					(p) => p.messageTemplateId === message.messageTemplateId,
				);

				if (experimentalMessage) {
					const cohort = FeatureGates.getExperimentValue<string>(
						experimentalMessage.statsigExperiment,
						experimentalMessage.statsigParameterName,
						'not-enrolled',
						{ fireExperimentExposure: true },
					);

					if (experimentalMessage?.statsigAAExperiment) {
						FeatureGates.getExperimentValue<string>(
							experimentalMessage.statsigAAExperiment,
							experimentalMessage.statsigParameterName,
							'not-enrolled',
							{ fireExperimentExposure: true },
						);
					}

					if (cohort === experimentalMessage.statsigTestCohort) {
						selectedMessage = message;
						break;
					}
				} else {
					selectedMessage = message;
					break;
				}
			}
			setLocalMessageState(selectedMessage ? [selectedMessage] : []);
		}

		if (isEPExternalMessageSSFEnabled && messagesState.data?.externalMessages?.length) {
			setLocalExternalMessageState(messagesState.data.externalMessages);
		}
	}, [messagesState.data]);

	const { triggerStates: epTriggerStates, EpTriggerWrapper } = useEpMigrationTriggers(
		isEPExternalMessageSSFEnabled ? filteredExternalMessages : undefined,
	);

	// find if an EP message has been triggered
	const activeEpMessageInstanceId = isEPExternalMessageSSFEnabled
		? Object.keys(epTriggerStates).find(
				(messageInstanceId) => epTriggerStates[messageInstanceId] === true,
			)
		: undefined;

	// Check for EP External messages first because they are not rendered until triggered from the front-end
	// and they will not be rendered if a non-ep message is currently rendered due to the choreographer
	const [currentLocalMessage] = filteredMessages ?? [];

	const currentExternalMessage = filteredExternalMessages?.find(
		(msg) => msg.messageInstanceId === activeEpMessageInstanceId,
	);

	const activeExternalMessage =
		isEPExternalMessageSSFEnabled && activeEpMessageInstanceId
			? currentExternalMessage
			: currentLocalMessage;

	const messageToRender = currentLocalMessage ?? activeExternalMessage;

	const content = FeatureGates.checkGate('post-office-screen-space-flags-shadow') ? (
		<SuppressIfModalIsPresent>
			{messageToRender ? (
				<MessageRenderer
					{...messageToRender}
					key={messageToRender.messageInstanceId}
					id={messageToRender.messageInstanceId}
					messageInstanceId={messageToRender.messageInstanceId}
					onDismissed={onDismissed}
					SuspenseBoundary={MaybeSuspenseBoundary}
					jiraFlagsService={jiraFlagsService}
					onError={reportUnexpectedError}
					reportExpectedError={reportExpectedError}
				/>
			) : null}
		</SuppressIfModalIsPresent>
	) : null;

	// Wrap content with trigger tracker components if feature gate is enabled
	return isEPExternalMessageSSFEnabled ? <EpTriggerWrapper>{content}</EpTriggerWrapper> : content;
};
